import { getApp, getApps, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getAnalytics, logEvent, setConsent } from "firebase/analytics";
import type { Analytics } from "firebase/analytics";

// Generated at build time by scripts/fetch-secrets.sh from GCP Secret Manager.
// If the file doesn't exist yet, run: bash scripts/fetch-secrets.sh
import { firebaseConfig } from "./__generated_firebase_config";

// Guarded init (glowtest-aligned resilience, project100 D-007). The getApps() guard
// makes a second import harmless instead of a double-initialize crash.
//
// ⚠️ THIS COMMENT HAS NOW BEEN WRONG IN BOTH DIRECTIONS, WHICH IS THE LESSON. It first read "A
// stale duplicate init module (src/cfg/firebase.ts) was removed 2026-09-05" — and the file was
// STILL ON DISK, unreferenced, calling `getAnalytics(app)`, with that sentence as the only
// reference to it anywhere in the tree; the deletion had been read as done for three weeks.
// Corrected on 2026-09-25 to say it was still there. Then the PO ruled "cleanup is always good"
// and it was ACTUALLY deleted the same day — so this note now describes a file that is genuinely
// gone, and the grep that proves it is `git grep cfg/firebase`. A claim about a file is only
// ever checked by opening the file, or by looking for it.
// Firebase is the identity backbone of a login-first app, so an invalid/stale
// generated config fails LOUDLY with the fix (deviation from glowtest, which can
// skip init because its content surfaces run without auth).
if (
  !firebaseConfig?.apiKey ||
  firebaseConfig.apiKey.includes("your-") ||
  firebaseConfig.apiKey.includes("placeholder")
) {
  throw new Error(
    "[FIREBASE] Invalid firebaseConfig in __generated_firebase_config.ts. " +
      "Generate it first: cd raisy_FE && bash scripts/fetch-secrets.sh",
  );
}
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(app);

// ── ANALYTICS RUNS WHERE IT CAN WORK, NOT WHERE IT CANNOT (T0409, decided 2026-09-25) ─────
// Firebase Analytics keeps its identifiers in cookies scoped to the REGISTRABLE DOMAIN, and
// `web.app` is a PUBLIC SUFFIX — so on the DEV host (`raisy-dev-3a562.web.app`) the browser
// refuses every `_ga` cookie and this integration can never work there. It was nevertheless
// initialised on every single load, which bought nothing and filled the console with those
// rejections.
//
// This is the option ALREADY NAMED for T0409 — "none on DEV" — not a new one. Nothing is lost on
// DEV because nothing worked there, and on a real domain (`raisy.team`) the behaviour is
// byte-for-byte what it was. It is also the reversible half of that fork: the other option, a
// custom domain for DEV, is infrastructure rather than a code change.
const ANALYTICS_HOSTS_WITHOUT_A_REGISTRABLE_DOMAIN = [/(^|\.)web\.app$/i];
const analyticsHost = typeof window === "undefined" ? "" : window.location.hostname;
const analyticsCapable =
  !!analyticsHost &&
  analyticsHost !== "localhost" &&
  analyticsHost !== "127.0.0.1" &&
  !ANALYTICS_HOSTS_WITHOUT_A_REGISTRABLE_DOMAIN.some((re) => re.test(analyticsHost));

/** `null` WHERE ANALYTICS CANNOT WORK (see above). Every consumer has to tolerate that. */
export const analytics: Analytics | null = analyticsCapable ? getAnalytics(app) : null;

// ---------------------------------------------------------------------------
// Google Consent Mode (project100 D-005)
// ---------------------------------------------------------------------------
// Firebase Analytics is initialized above, but the SDK is told to run in
// "denied" mode until the visitor actually consents in the cookie notice.
// While denied, Analytics stores no cookies/identifiers and does no ad
// personalization (cookieless aggregate counting only). When the visitor
// clicks Accept (or saves preferences), applyAnalyticsConsent() flips the
// relevant storage types to "granted" live — no reload needed.

export type AnalyticsConsent = {
  necessary?: boolean;
  personalization?: boolean;
  marketing?: boolean;
  statistics?: boolean;
};

export const CONSENT_COOKIE_NAME = "cookie_consent";

export function readConsentCookie(): AnalyticsConsent | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${CONSENT_COOKIE_NAME}=`));
  if (!match) return null;
  try {
    return JSON.parse(
      decodeURIComponent(match.slice(CONSENT_COOKIE_NAME.length + 1)),
    ) as AnalyticsConsent;
  } catch {
    return null;
  }
}

const toConsentState = (granted?: boolean): "granted" | "denied" =>
  granted ? "granted" : "denied";

/**
 * Applies the current consent choice to Firebase Analytics (Google Consent
 * Mode). With no argument it reads the `cookie_consent` cookie; if there is no
 * choice yet, everything non-necessary stays DENIED.
 */
export function applyAnalyticsConsent(consent?: AnalyticsConsent | null): void {
  if (typeof window === "undefined") return;
  // No Analytics instance means no gtag layer to carry the state. Returning early is the honest
  // no-op: recording a consent choice for an integration that was never initialised would store
  // a decision nothing can honour.
  if (!analytics) return;
  const c = consent ?? readConsentCookie();
  const statisticsGranted = !!c?.statistics;
  const marketingGranted = !!c?.marketing;
  const personalizationGranted = !!c?.personalization;
  try {
    setConsent({
      analytics_storage: toConsentState(statisticsGranted),
      ad_storage: toConsentState(marketingGranted),
      ad_user_data: toConsentState(marketingGranted),
      ad_personalization: toConsentState(marketingGranted),
      personalization_storage: toConsentState(personalizationGranted),
      // Strictly necessary for the site to operate (auth/security/language):
      functionality_storage: "granted",
      security_storage: "granted",
    });
  } catch (e) {
    console.warn("[CONSENT][applyAnalyticsConsent_010] failed to set consent", e);
  }
}

// Apply the stored choice (or default to denied) as early as possible.
applyAnalyticsConsent();

// Helper function to log custom events
export const logCustomEvent = (eventName: string, eventParams?: Record<string, any>) => {
  if (!analytics) return;
  logEvent(analytics, eventName, eventParams);
};
